import React from 'react';
import { Card, Row, Col, Typography, Button, Upload, Input, Space, Dropdown, message, theme, Select } from 'antd';
import { GlassCard } from '../../../../shared/components';
import { DownloadOutlined, UploadOutlined, DeleteOutlined, MoreOutlined, PaperClipOutlined, FileDoneOutlined, FileOutlined } from '@ant-design/icons';
import { useTranslate } from '@refinedev/core';
import { DateField } from '@refinedev/antd';
import { UploadProps } from 'antd';

const { Title } = Typography;
const { TextArea } = Input;

interface Attachment {
    id: string;
    name: string;
    file: {
        path: string;
        id: string;
    };
    createdAt: string;
    direction: 'in' | 'out';
    metadata?: string;
}

interface AttachmentMetadata {
    category?: string;
}

const UNCATEGORIZED_KEY = '__uncategorized__';

function getAttachmentCategory(attachment: Attachment): string {
    if (!attachment.metadata) return UNCATEGORIZED_KEY;
    try {
        const parsed = JSON.parse(attachment.metadata) as AttachmentMetadata;
        return typeof parsed?.category === 'string' && parsed.category.trim() !== ''
            ? parsed.category.trim()
            : UNCATEGORIZED_KEY;
    } catch {
        return UNCATEGORIZED_KEY;
    }
}

function groupAttachmentsByCategory(attachments: Attachment[]): Record<string, Attachment[]> {
    return attachments.reduce<Record<string, Attachment[]>>((acc, attachment) => {
        const category = getAttachmentCategory(attachment);
        if (!acc[category]) acc[category] = [];
        acc[category].push(attachment);
        return acc;
    }, {});
}

interface OrderAttachmentsProps {
    attachments: Attachment[];
    resultAttachments: Attachment[];
    isAdmin: boolean;
    serviceComments?: string;
    onServiceCommentsChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onServiceCommentsSubmit?: () => void;
    canSubmitServiceComments?: boolean;
    isSavingServiceComments?: boolean;
    onDeleteAttachment?: (attachment: Attachment) => void;
    fileUploadProps?: UploadProps;
    incomingFileUploadProps?: UploadProps;
    incomingCategory?: 'bin' | 'misc';
    onIncomingCategoryChange?: (category: 'bin' | 'misc') => void;
}

export const OrderAttachments: React.FC<OrderAttachmentsProps> = ({
    attachments,
    resultAttachments,
    isAdmin,
    serviceComments,
    onServiceCommentsChange,
    onServiceCommentsSubmit,
    canSubmitServiceComments,
    isSavingServiceComments,
    onDeleteAttachment,
    fileUploadProps,
    incomingFileUploadProps,
    incomingCategory,
    onIncomingCategoryChange,
}) => {
    const _t = useTranslate();
    const { token } = theme.useToken();
    const [isIncomingUploadVisible, setIsIncomingUploadVisible] = React.useState(false);

    return (
        <>
            <Title level={4} className="mt-6">
                <PaperClipOutlined className="mr-2" style={{ color: token.colorPrimary }}/>
                {_t("orders.titles.attachments")}
            </Title>

            <GlassCard className="mb-4">

            <Row gutter={[{ xs: 8, sm: 16, md: 24 }, 16]} >
                <Col xs={24}>
                    <Row gutter={[24, 16]}>
                        {/* Left Column - Incoming Attachments */}
                        <Col xs={24} lg={12}>
                            <div className="border-r pr-6" style={{ borderColor: token.colorBorder }}>
                                <Title level={5} style={{ marginBottom: 16 }}>
                                    <DownloadOutlined className="mr-2" />
                                    {_t("orders.titles.incomingFiles")}
                                </Title>

                                {attachments && attachments.length > 0 ? (
                                    (() => {
                                        const byCategory = groupAttachmentsByCategory(attachments);
                                        const categoryKeys = Object.keys(byCategory).sort((a, b) => {
                                            if (a === UNCATEGORIZED_KEY) return 1;
                                            if (b === UNCATEGORIZED_KEY) return -1;
                                            return a.localeCompare(b);
                                        });
                                        return (
                                            <div className="flex flex-col gap-6">
                                                {categoryKeys.map((category) => (
                                                    <div key={category}>
                                                        <Typography.Text
                                                            strong
                                                            className="block mb-3 text-sm"
                                                            style={{ color: token.colorTextSecondary }}
                                                        >
                                                            {category === UNCATEGORIZED_KEY
                                                                ? _t("orders.titles.categoryUncategorized")
                                                                : category === "misc"
                                                                    ? _t("orders.titles.categoryMisc")
                                                                    : category === "bin"
                                                                        ? _t("orders.titles.categoryBin")
                                                                        : category}
                                                        </Typography.Text>
                                                        <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
                                                            {byCategory[category].map((attachment: Attachment) => (
                                                                <Card
                                                                    key={attachment.id}
                                                                    hoverable
                                                                    size="small"
                                                                    className="attachment-card cursor-pointer shadow-md"
                                                                    onClick={() => window.open(attachment.file.path, "_blank")}
                                                                >
                                                                    <div className="flex items-center space-x-3">
                                                                        <div className="flex-shrink-0">
                                                                            <FileOutlined
                                                                                style={{
                                                                                    fontSize: 24,
                                                                                    color: token.colorPrimary
                                                                                }} />
                                                                        </div>
                                                                        <div className="flex-1 min-w-0">
                                                                            <Typography.Text
                                                                                strong
                                                                                className="block truncate"
                                                                                title={attachment.name}
                                                                            >
                                                                                {attachment.name}
                                                                            </Typography.Text>
                                                                            <DateField
                                                                                value={attachment.createdAt}
                                                                                format="DD/MM/YYYY HH:mm"
                                                                                className="text-xs"
                                                                                style={{ color: token.colorTextSecondary }}
                                                                            />
                                                                        </div>
                                                                        {isAdmin && (
                                                                            <div className="flex-shrink-0">
                                                                                <Dropdown
                                                                                    menu={{
                                                                                        items: [
                                                                                            {
                                                                                                key: 'delete',
                                                                                                label: _t('buttons.delete'),
                                                                                                icon: <DeleteOutlined />,
                                                                                                danger: true,
                                                                                                onClick: () => onDeleteAttachment?.(attachment)
                                                                                            }
                                                                                        ]
                                                                                    }}
                                                                                    trigger={['click']}
                                                                                >
                                                                                    <Button
                                                                                        type="text"
                                                                                        icon={<MoreOutlined />}
                                                                                        size="small"
                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                    />
                                                                                </Dropdown>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </Card>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })()
                                ) : (
                                    <div className="text-center py-8" style={{ color: token.colorTextSecondary }}>
                                        <DownloadOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                                        <div>{_t("orders.titles.noIncomingFiles")}</div>
                                    </div>
                                )}

                                {isAdmin && incomingFileUploadProps && (
                                    <div className="mt-4">
                                        {isIncomingUploadVisible && (
                                            <>
                                                <div className="mb-3">
                                                    <Typography.Text
                                                        className="block mb-2 text-sm"
                                                        style={{ color: token.colorTextSecondary }}
                                                    >
                                                        {_t("orders.titles.incomingCategory")}
                                                    </Typography.Text>
                                                    <Select
                                                        className="w-full"
                                                        value={incomingCategory}
                                                        onChange={onIncomingCategoryChange}
                                                        options={[
                                                            { value: 'misc', label: _t("orders.titles.categoryMisc") },
                                                            { value: 'bin', label: _t("orders.titles.categoryBin") },
                                                        ]}
                                                    />
                                                </div>
                                                <Upload.Dragger
                                                    {...incomingFileUploadProps}
                                                    showUploadList={true}
                                                >
                                                    <p className="ant-upload-drag-icon">
                                                        <DownloadOutlined style={{ fontSize: 48, color: token.colorPrimary }} />
                                                    </p>
                                                    <p className="ant-upload-text font-semibold" style={{ color: token.colorText }}>
                                                        {_t('orders.titles.upload')}
                                                    </p>
                                                    <p className="ant-upload-hint text-sm" style={{ color: token.colorTextSecondary }}>
                                                        {_t('orders.titles.uploadIncomingFile')}
                                                    </p>
                                                </Upload.Dragger>
                                            </>
                                        )}
                                        <div className="mt-2">
                                            <Button
                                                type="link"
                                                size="small"
                                                className="p-0"
                                                onClick={() => setIsIncomingUploadVisible((prev) => !prev)}
                                            >
                                                {isIncomingUploadVisible
                                                    ? _t("orders.titles.hideIncomingUpload")
                                                    : _t("orders.titles.showIncomingUpload")}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Col>

                        {/* Right Column - Outgoing Attachments */}
                        <Col xs={24} lg={12}>
                            <div className="pl-6">
                                <Title level={5} style={{ marginBottom: 16, color: token.colorPrimary }}>
                                    <UploadOutlined className="mr-2" />
                                    {_t("orders.titles.outgoingFiles")}
                                </Title>

                                {resultAttachments && resultAttachments.length > 0 ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
                                        {resultAttachments.map((attachment: Attachment) => (
                                            <Card
                                                key={attachment.id}
                                                hoverable
                                                size="small"
                                                className="attachment-card cursor-pointer shadow-md"
                                                onClick={() => window.open(attachment.file.path, "_blank")}
                                            >
                                                <div className="flex items-center space-x-3">
                                                    <div className="flex-shrink-0">
                                                        <FileDoneOutlined
                                                            style={{
                                                                fontSize: 24,
                                                                color: token.colorPrimary
                                                            }} />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <Typography.Text
                                                            strong
                                                            className="block truncate"
                                                            title={attachment.name}
                                                        >
                                                            {attachment.name}
                                                        </Typography.Text>

                                                        <DateField
                                                            value={attachment.createdAt}
                                                            format="DD/MM/YYYY HH:mm"
                                                            className="text-xs"
                                                            style={{ color: token.colorTextSecondary }}
                                                        />

                                                    </div>
                                                    {isAdmin && (
                                                        <div className="flex-shrink-0">
                                                            <Dropdown
                                                                menu={{
                                                                    items: [
                                                                        {
                                                                            key: 'delete',
                                                                            label: _t('buttons.delete'),
                                                                            icon: <DeleteOutlined />,
                                                                            danger: true,
                                                                            onClick: () => onDeleteAttachment?.(attachment)
                                                                        }
                                                                    ]
                                                                }}
                                                                trigger={['click']}
                                                            >
                                                                <Button
                                                                    type="text"
                                                                    icon={<MoreOutlined />}
                                                                    size="small"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                />
                                                            </Dropdown>
                                                        </div>
                                                    )}
                                                </div>
                                            </Card>
                                        ))}
                                    </div>
                                ) : (
                                    <></>
                                )}

                                {/* Upload Dragger - Always visible for admins */}
                                {isAdmin && fileUploadProps && (
                                    <div className="mt-4">
                                        <Upload.Dragger
                                            {...fileUploadProps}
                                            showUploadList={true}
                                        >
                                            <p className="ant-upload-drag-icon">
                                                <UploadOutlined style={{ fontSize: 48, color: token.colorPrimary }} />
                                            </p>
                                            <p className="ant-upload-text font-semibold" style={{ color: token.colorText }}>
                                                {_t('orders.titles.upload')}
                                            </p>
                                            <p className="ant-upload-hint text-sm" style={{ color: token.colorTextSecondary }}>
                                                {_t('orders.titles.uploadFile')}
                                            </p>
                                        </Upload.Dragger>
                                    </div>
                                )}
                            </div>

                            <div className="w-full mt-8 px-6">
                                {isAdmin && (
                                    <>
                                        <TextArea
                                            className="w-full"
                                            placeholder={_t("orders.fields.comments")}
                                            value={serviceComments}
                                            rows={3}
                                            onChange={onServiceCommentsChange}
                                        />
                                        <div className="mt-2 flex justify-end">
                                            <Button
                                                type="primary"
                                                onClick={onServiceCommentsSubmit}
                                                disabled={!canSubmitServiceComments || isSavingServiceComments}
                                                loading={isSavingServiceComments}
                                            >
                                                {_t("orders.actions.saveServiceComments")}
                                            </Button>
                                        </div>
                                    </>
                                )}
                                {!isAdmin && serviceComments && (
                                    <>
                                        <span className="font-bold">Commentaires</span> : {serviceComments}
                                    </>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Col>
            </Row>
            </GlassCard>
        </>
    );
}; 